/**
 * PledgesDashboard - Fulfilment analytics for a campaign's pledges.
 *
 * Backed by GET /campaigns/{id}/pledges/dashboard which returns:
 *   totals: {pledged, paid, outstanding, fulfilment_pct, count}
 *   aging:  {upcoming, overdue_0_30, overdue_31_60, overdue_60_plus, no_date}
 *   top_overdue: [{id, donor_name, remaining, currency, days_overdue}]
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { AlertTriangle, TrendingUp, Clock, Bell, Loader2, Mail } from 'lucide-react';

const fmt = (n, ccy) => `${ccy || ''} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();

const AGING_META = [
  { key: 'upcoming', label: 'Upcoming', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { key: 'overdue_0_30', label: '0-30 days overdue', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { key: 'overdue_31_60', label: '31-60 days overdue', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { key: 'overdue_60_plus', label: '60+ days overdue', color: 'bg-red-100 text-red-700 border-red-200' },
  { key: 'no_date', label: 'No due date', color: 'bg-slate-100 text-slate-700 border-slate-200' },
];

const PledgesDashboard = ({ campaignId }) => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: ['pledges-dashboard', campaignId],
    queryFn: async () => (await api.get(`/campaigns/${campaignId}/pledges/dashboard`)).data,
    enabled: !!campaignId,
    retry: 1,
  });

  const remindOne = useMutation({
    mutationFn: async (pledgeId) => (await api.post(`/pledges/${pledgeId}/remind`)).data,
    onSuccess: (r) => {
      toast.success(`Reminder sent · email ${r.email_sent ? '✓' : '×'} · in-app ${r.notification_sent ? '✓' : '×'}`);
      queryClient.invalidateQueries(['pledges-dashboard', campaignId]);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Reminder failed')),
  });

  const bulkRemind = useMutation({
    mutationFn: async () => (await api.post(`/campaigns/${campaignId}/pledges/remind-overdue`, {
      only_overdue: true,
      channels: ['email', 'in_app'],
      include_upcoming_days: 7,
    })).data,
    onSuccess: (r) => {
      toast.success(`${r.total_targeted} pledger(s) nudged · ${r.email_sent} email · ${r.notifications_sent} in-app`);
      queryClient.invalidateQueries(['pledges-dashboard', campaignId]);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Bulk reminder failed')),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="pledges-dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="border-red-200">
        <CardContent className="py-10 text-center space-y-3">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto" />
          <p className="text-sm text-slate-600">{getErrorMessage(error, "Couldn't load dashboard.")}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="dashboard-retry">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  const { totals = {}, aging = {}, top_overdue: topOverdue = [], campaign_currency: ccy = 'USD' } = data || {};
  const pct = Math.min(100, Math.max(0, Number(totals.fulfilment_pct || 0)));

  return (
    <div className="space-y-6" data-testid="pledges-dashboard">
      {/* Fulfilment ring + totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              Fulfilment · {pct}%
            </CardTitle>
            <CardDescription>Total pledged vs paid</CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={pct} className="h-3" data-testid="fulfilment-progress" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4 text-sm">
              <div>
                <p className="text-muted-foreground">Pledged</p>
                <p className="text-xl font-semibold" data-testid="totals-pledged">{fmt(totals.pledged, ccy)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Paid</p>
                <p className="text-xl font-semibold text-emerald-600" data-testid="totals-paid">{fmt(totals.paid, ccy)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Outstanding</p>
                <p className="text-xl font-semibold text-orange-600" data-testid="totals-outstanding">{fmt(totals.outstanding, ccy)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="h-4 w-4 text-orange-500" />
              Bulk Reminders
            </CardTitle>
            <CardDescription>Nudge every overdue + upcoming (7d) pledge</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => bulkRemind.mutate()}
              disabled={bulkRemind.isPending}
              className="w-full"
              data-testid="bulk-remind-btn"
            >
              {bulkRemind.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Send email + in-app
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Uses your campaign&apos;s <span className="font-medium">pledge_reminder</span> template if defined.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Aging buckets */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-500" />
            Aging buckets
          </CardTitle>
          <CardDescription>Outstanding pledges grouped by expected date</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {AGING_META.map(({ key, label, color }) => {
              const row = aging[key] || { count: 0, amount: 0 };
              return (
                <div
                  key={key}
                  className={`border rounded-lg p-3 ${color}`}
                  data-testid={`aging-${key}`}
                >
                  <p className="text-xs font-medium">{label}</p>
                  <p className="text-2xl font-bold mt-1">{row.count}</p>
                  <p className="text-xs mt-1">{fmt(row.amount, ccy)}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Top overdue */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top overdue pledges</CardTitle>
          <CardDescription>Longest outstanding, ready for a nudge</CardDescription>
        </CardHeader>
        <CardContent>
          {topOverdue.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              🎉 No overdue pledges — everyone is on track.
            </div>
          ) : (
            <div className="space-y-2">
              {topOverdue.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between border rounded-lg p-3"
                  data-testid={`overdue-row-${p.id}`}
                >
                  <div>
                    <p className="font-medium">{p.donor_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.donor_email || 'no email'} · {p.expected_date || 'no date'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-medium">{fmt(p.remaining, p.currency)}</p>
                      <Badge variant="destructive" className="text-xs mt-1">{p.days_overdue}d overdue</Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => remindOne.mutate(p.id)}
                      disabled={remindOne.isPending}
                      data-testid={`remind-${p.id}`}
                    >
                      <Bell className="h-3 w-3 mr-1" /> Remind
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PledgesDashboard;
